import { Component, h, Element, Host, Prop } from '@stencil/core';
import { HTMLAnchorElementTarget, ButtonColor, hasShadowDom } from '../../utils/helpers';

@Component({
  tag: 'vds-button',
  styleUrl: 'vds-button.scss',
  shadow: true,
})
export class VdsButton {

  @Element() el!: HTMLElement;

  /**
   * Behavior of the icon (showing always or only on hover)
   */
  @Prop() iconBehaviour: 'visible' | 'hover' | 'hidden' = 'hover';

  /**
   * Button coloring
   */
  @Prop() color: ButtonColor = 'default';

  /**
   * The type of the button.
   */
  @Prop() type: 'submit' | 'button' = 'button';

  /**
   * Label of the button
   */
  @Prop() label: string;

  /**
   * Toggles disabled status on and off
   */
  @Prop({ reflect: true, mutable: true }) disabled = false;

  /**
   * Href of anchor
   */
  @Prop() href: string;

  /**
   * Target of anchor
   */
  @Prop() target: HTMLAnchorElementTarget = '_self';

  /**
   * Sets a min-width for the button to apply a standard width
   */
  @Prop() expand: boolean = false;

  /**
   * Toggles background tranparency
   */
  @Prop() transparent: boolean = false;

  private handleClick = (ev: Event) => {
    if (this.type === 'submit' && hasShadowDom(this.el)) {
      const form = this.el.closest('form');
      if (form) {
        ev.preventDefault();

        const fakeButton = document.createElement('button');
        fakeButton.type = this.type;
        fakeButton.style.display = 'none';
        form.appendChild(fakeButton);
        fakeButton.click();
        fakeButton.remove();
      }
    }
  }

  render() {

    const { disabled, color, iconBehaviour, label, href, target, expand, transparent } = this;
    const TagType = this.href === undefined ? 'button' : 'a' as any;

    return(
      <Host
        onClick={this.handleClick}
        class={{
          'button-disabled': disabled,
          'w-full': expand
        }}>
        <TagType
        part="button"
        class={{
          "inline-block border-solid text-center no-underline m-0 normal-case overflow-visible box-border align-middle sm:leading-[36px] leading-[26px] font-bold rounded-[22px] border-2 text-[15px] transition-all ease-out bg-left-top disabled:text-grey-lighter disabled:border-grey-lighter": true,
          "button-default": color === "default",
          "button-berry": color === "berry",
          "button-transparent": transparent,
          "button-icon-visible": iconBehaviour === "visible",
          "button-icon-hover": iconBehaviour === "hover",
          "button-icon-hidden": iconBehaviour === "hidden",
          "min-w-[100px] w-full": expand
        }}
        type="button"
        disabled={ disabled }
        href={ href }
        target={ target }>
          <span class="button-label whitespace-nowrap">
            { label }
          </span>
          <span class="button-icon relative text-right -top-[2px]">
            <slot name="icon"></slot>
          </span>
      </TagType>
    </Host>
    );
  }

  async componentDidRender() : Promise<void> {
    this.setSvgStyles();
  }

  private setSvgStyles() {
    const svg = this.el.querySelector('svg');
    if (svg) {
      svg.style.display = `initial`;
      svg.style.verticalAlign = `middle`;
      svg.style.overflow = `visible`;
    }
  }
}
